/**
 * The post-event questionnaire, filed by the coach who worked the event.
 *
 * It is an upsert on (event, coach), so this is also the edit form: a coach who
 * remembers something on the drive home can come back and add it. Only the two
 * things the business actually needs are pushed for, the rest is optional, and
 * an incident cannot be recorded without a description because
 * submitPostEventReport() refuses it.
 */

import { useRef, useState } from 'react'
import { submitPostEventReport } from '../../lib/api'
import { Card } from '../../components/ui'
import { formatDateTime } from '../../lib/format'
import { useAction } from './coachHooks'
import { ActionFeedback } from './coachUi'
import type { PostEventReport } from '../../types/db'

const RATINGS = [1, 2, 3, 4, 5]

export function EventReportForm({
  eventId,
  coachId,
  existing,
  onSaved,
}: {
  eventId: string
  coachId: string
  existing: PostEventReport | null
  onSaved: () => void
}) {
  const action = useAction(onSaved)
  const [rating, setRating] = useState<number | null>(existing?.overall_rating ?? null)
  const [attendance, setAttendance] = useState(
    existing?.attendance_count === null || existing?.attendance_count === undefined
      ? ''
      : String(existing.attendance_count),
  )
  const [wentWell, setWentWell] = useState(existing?.went_well ?? '')
  const [issues, setIssues] = useState(existing?.issues ?? '')
  const [incident, setIncident] = useState(existing?.incident ?? false)
  const [incidentDetails, setIncidentDetails] = useState(existing?.incident_details ?? '')
  const [clientFeedback, setClientFeedback] = useState(existing?.client_feedback ?? '')
  const [ratingError, setRatingError] = useState<string | null>(null)
  const [numberError, setNumberError] = useState<string | null>(null)
  const [incidentError, setIncidentError] = useState<string | null>(null)
  // A double tap on a slow connection must not send the report twice. State
  // is too slow for that (the second tap lands before the re-render), a ref
  // is not.
  const inFlight = useRef(false)

  const trimmed = (value: string): string | null => (value.trim() ? value.trim() : null)

  function submit() {
    if (inFlight.current) return

    // The two things the business actually needs, plus the one thing that
    // cannot be left half said. Everything else is optional.
    const count = attendance.trim() === '' ? null : Number(attendance)
    const ratingProblem = rating === null ? 'Pick a number from 1 to 5.' : null
    const countProblem =
      count === null
        ? 'Put the number of children, or 0 if nobody came.'
        : !Number.isInteger(count) || count < 0
          ? 'Write the number of children as digits, for example 18.'
          : null
    const incidentProblem =
      incident && !trimmed(incidentDetails)
        ? 'Say what happened, or switch this back to No.'
        : null
    setRatingError(ratingProblem)
    setNumberError(countProblem)
    setIncidentError(incidentProblem)
    if (ratingProblem || countProblem || incidentProblem) return

    inFlight.current = true
    void action
      .run(
        () =>
          submitPostEventReport({
            eventId,
            coachId,
            overallRating: rating,
            attendanceCount: count,
            wentWell: trimmed(wentWell),
            issues: trimmed(issues),
            incident,
            incidentDetails: trimmed(incidentDetails),
            clientFeedback: trimmed(clientFeedback),
            photoPaths: existing?.photo_paths ?? [],
          }),
        existing ? 'Report updated. Thank you.' : 'Report sent. Thank you.',
      )
      .finally(() => {
        inFlight.current = false
      })
  }

  const hasFieldErrors = Boolean(ratingError || numberError || incidentError)

  return (
    <Card>
      {existing ? (
        <p className="small muted" style={{ marginBottom: 'var(--sp-4)' }}>
          You filed this on {formatDateTime(existing.submitted_at)}. Changing anything below
          replaces it.
        </p>
      ) : (
        <p className="small muted" style={{ marginBottom: 'var(--sp-4)' }}>
          Two minutes, while it is fresh. Michel reads these, and they are what the client hears
          back.
        </p>
      )}

      <div className="field">
        <label id="rating-label">How did the event go?</label>
        <div
          className="btn-group"
          role="group"
          aria-labelledby="rating-label"
          style={{ flexWrap: 'nowrap' }}
        >
          {RATINGS.map((n) => (
            <button
              key={n}
              type="button"
              className={`btn${rating === n ? '' : ' btn--secondary'}`}
              aria-pressed={rating === n}
              disabled={action.busy}
              style={{ flex: '1 1 0', minWidth: 0, padding: 0 }}
              onClick={() => setRating(rating === n ? null : n)}
            >
              {n}
            </button>
          ))}
        </div>
        {ratingError ? <p className="field__error">{ratingError}</p> : null}
        <p className="field__help">1 is rough, 5 is excellent. Required.</p>
      </div>

      <div className="field">
        <label htmlFor="report-attendance">How many children took part?</label>
        <input
          id="report-attendance"
          type="number"
          inputMode="numeric"
          min={0}
          value={attendance}
          onChange={(e) => setAttendance(e.target.value)}
          placeholder="18"
        />
        {numberError ? <p className="field__error">{numberError}</p> : null}
        <p className="field__help">Required. Put 0 if nobody came.</p>
      </div>

      <div className="field">
        <label htmlFor="report-went-well">What went well?</label>
        <textarea
          id="report-went-well"
          value={wentWell}
          onChange={(e) => setWentWell(e.target.value)}
          placeholder="The relay filled the last half hour and the parents joined in."
        />
      </div>

      <div className="field">
        <label htmlFor="report-issues">Anything that slowed you down?</label>
        <textarea
          id="report-issues"
          value={issues}
          onChange={(e) => setIssues(e.target.value)}
          placeholder="Speaker battery was flat on arrival."
        />
        <p className="field__help">
          Say it plainly. This is how kit and briefs get fixed, not a mark against you.
        </p>
      </div>

      <div className="field">
        <label id="incident-label">Was there an incident?</label>
        <div className="btn-group" role="group" aria-labelledby="incident-label">
          <button
            type="button"
            className={`btn${incident ? ' btn--secondary' : ''}`}
            aria-pressed={!incident}
            disabled={action.busy}
            style={{ flex: '1 1 0' }}
            onClick={() => setIncident(false)}
          >
            No
          </button>
          <button
            type="button"
            className={`btn${incident ? ' btn--accent' : ' btn--secondary'}`}
            aria-pressed={incident}
            disabled={action.busy}
            style={{ flex: '1 1 0' }}
            onClick={() => setIncident(true)}
          >
            Yes
          </button>
        </div>
        <p className="field__help">
          Injury, a child leaving with the wrong adult, anything a parent may call about.
        </p>
      </div>

      {incident ? (
        <div className="field">
          <label htmlFor="report-incident">What happened?</label>
          <textarea
            id="report-incident"
            value={incidentDetails}
            onChange={(e) => setIncidentDetails(e.target.value)}
            placeholder="Time, who was involved, what you did, who you told."
          />
          {incidentError ? <p className="field__error">{incidentError}</p> : null}
          <p className="field__help">Required. A flagged incident with no detail helps nobody.</p>
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="report-client">Anything the client said?</label>
        <textarea
          id="report-client"
          value={clientFeedback}
          onChange={(e) => setClientFeedback(e.target.value)}
          placeholder="The mother asked whether we do school holiday camps."
        />
      </div>

      {hasFieldErrors ? (
        <p className="field__error" role="alert" style={{ marginBottom: 'var(--sp-2)' }}>
          Something above needs a look before this can go.
        </p>
      ) : null}

      <button
        type="button"
        className="btn btn--block"
        disabled={action.busy}
        onClick={submit}
      >
        {action.busy ? 'Sending' : existing ? 'Save changes' : 'Send report'}
      </button>

      <p className="faint" style={{ marginTop: 'var(--sp-3)' }}>
        Photos get added here once file storage is switched on.
      </p>

      <ActionFeedback error={action.error} done={action.done} />
    </Card>
  )
}
